import {
  Invitation,
  Inviter,
  Message,
  Notification,
  Registerer,
  RegistererState,
  Session,
  SessionState,
  UserAgent,
  type UserAgentOptions,
} from "sip.js";
import type { SessionDescriptionHandlerModifier } from "sip.js/lib/api/session-description-handler";
import { holdModifier } from "sip.js/lib/platform/web/modifiers/modifiers";

import {
  buildSipUri,
  resolveSipIceServers,
  resolveSipAudioCodecs,
} from "~/services/calling/config";
import { isRegisterInProgressError } from "~/services/calling/sipErrors";
import type {
  CallIncomingPayload,
  SipCallError,
  SipCallState,
  SipConnectLoggingContext,
  SipCredentials,
  SipRegistrationStatus,
  SuperviseMode,
} from "~/services/calling/types";

export type SipAnswerOptions = {
  incomingId?: string;
  superviseMode?: SuperviseMode;
};

export type SipIncomingSignalSource =
  | "sip-invite"
  | "sip-message"
  | "sip-notify";

type SipPhoneEvents = {
  onRegistrationStatus?: (status: SipRegistrationStatus) => void;
  onTransportStateChange?: (state: "connected" | "disconnected") => void;
  onCallStateChange?: (
    state: SipCallState,
    session: Session | null,
    direction?: "incoming" | "outgoing"
  ) => void;
  onIncomingCall?: (fromUri: string | null) => void;
  onIncomingCallPayload?: (
    payload: CallIncomingPayload,
    source: SipIncomingSignalSource
  ) => void;
  onInvitationEnded?: (correlatorIds: string[]) => void;
  onBackgroundHeldEnded?: () => void;
  onSessionsSwapped?: (payload: {
    foregroundDirection: "incoming" | "outgoing";
    backgroundDirection: "incoming" | "outgoing";
  }) => void;
  onError?: (error: Error) => void;
};

type PendingInvitationEntry = {
  invitation: Invitation;
  correlatorIds: Set<string>;
};

type ConnectOptions = {
  sipWssUrl: string;
  sipDomain: string;
  credentials: SipCredentials;
};

const defaultAudioCodecKeys = ["opus", "pcmu", "pcma"] as const;
const telephoneEventCodecKey = "telephone-event";
const dtmfToneRegex = /^[0-9*#ABCD]$/i;

function normalizeCodecKey(codec: string) {
  const normalized = codec.trim().toLowerCase();
  if (!normalized) return "";
  if (
    normalized === "ulaw" ||
    normalized === "g711u" ||
    normalized === "g.711u"
  ) {
    return "pcmu";
  }
  if (
    normalized === "alaw" ||
    normalized === "g711a" ||
    normalized === "g.711a"
  ) {
    return "pcma";
  }
  if (normalized === "dtmf") {
    return telephoneEventCodecKey;
  }
  return normalized;
}

export class SipPhone {
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private session: Session | null = null;
  /** Established inbound leg parked on SIP hold while a foreground outbound runs. */
  private heldSession: Session | null = null;
  private isHeldSessionOnHold = false;
  private sessionDirections = new WeakMap<Session, "incoming" | "outgoing">();
  private inviter: Inviter | null = null;
  private pendingInvitations = new Map<string, PendingInvitationEntry>();
  private audioElement: HTMLAudioElement | null = null;
  private reconnectTimer: number | null = null;
  private registerRetryTimer: number | null = null;
  private registerRetryAttempt = 0;
  private lastRegisterRejectStatusCode: number | null = null;
  private lastRegisterRejectReasonPhrase: string | null = null;
  private shouldReconnect = true;
  private stopPromise: Promise<void> | null = null;
  private connectAndRegisterPromise: Promise<void> | null = null;
  private registerInFlightPromise: Promise<void> | null = null;
  /** True after register() resolves without error for the current UA session. */
  private registerRequestCompleted = false;
  private lastConnectOptions: ConnectOptions | null = null;
  private events: SipPhoneEvents;
  private readonly bodyKeyLookupCache = new WeakMap<
    Record<string, unknown>,
    Map<string, unknown>
  >();
  private readonly audioCodecKeys = this.resolveAudioCodecKeys();
  private readonly audioCodecModifier = this.buildAudioCodecModifier();
  private readonly listenDirectionModifier =
    this.buildAudioDirectionModifier("recvonly");
  private readonly bargeDirectionModifier =
    this.buildAudioDirectionModifier("sendrecv");
  private pendingAnswerSuperviseMode: SuperviseMode | null = null;

  constructor(events: SipPhoneEvents = {}) {
    this.events = events;
  }

  private debug(message: string, data?: Record<string, unknown>) {
    void message;
    void data;
  }

  private logInboundAudioStats(pc: RTCPeerConnection) {
    window.setTimeout(() => {
      void pc
        .getStats()
        .then((stats) => {
          const reports = Array.from(stats.values()) as Array<Record<string, any>>;
          const inboundAudio = reports.find(
            (report) =>
              report?.type === "inbound-rtp" &&
              (report?.kind === "audio" || report?.mediaType === "audio")
          ) as
            | {
                packetsReceived?: number;
                bytesReceived?: number;
                jitter?: number;
                packetsLost?: number;
                totalAudioEnergy?: number;
                audioLevel?: number;
                codecId?: string;
              }
            | undefined;

          if (!inboundAudio) {
            const transport = reports.find((report) => report?.type === "transport") as
              | { bytesReceived?: number; bytesSent?: number; dtlsState?: string }
              | undefined;
            const selectedPair = reports.find(
              (report) =>
                report?.type === "candidate-pair" && report?.selected === true
            ) as
              | {
                  state?: string;
                  currentRoundTripTime?: number;
                  bytesReceived?: number;
                  bytesSent?: number;
                }
              | undefined;
            this.debug("audioStats:inbound:none", {
              transportBytesReceived: transport?.bytesReceived ?? null,
              transportBytesSent: transport?.bytesSent ?? null,
              dtlsState: transport?.dtlsState ?? null,
              selectedPairState: selectedPair?.state ?? null,
              selectedPairRtt: selectedPair?.currentRoundTripTime ?? null,
              selectedPairBytesReceived: selectedPair?.bytesReceived ?? null,
              selectedPairBytesSent: selectedPair?.bytesSent ?? null,
            });
            return;
          }

          this.debug("audioStats:inbound", {
            packetsReceived: inboundAudio.packetsReceived ?? null,
            bytesReceived: inboundAudio.bytesReceived ?? null,
            packetsLost: inboundAudio.packetsLost ?? null,
            jitter: inboundAudio.jitter ?? null,
            totalAudioEnergy: inboundAudio.totalAudioEnergy ?? null,
            audioLevel: inboundAudio.audioLevel ?? null,
            codecId: inboundAudio.codecId ?? null,
          });
        })
        .catch((error) => {
          this.debug("audioStats:error", {
            message: (error as Error)?.message ?? "unknown",
          });
        });
    }, 3000);
  }

  private summarizeSdp(sdp: string | undefined | null) {
    if (!sdp) {
      return {
        hasSdp: false,
        hasAudioMLine: false,
        candidateCount: 0,
        hasFingerprint: false,
        hasIceUfrag: false,
        hasIcePwd: false,
        hasSetup: false,
        hasRtcpMux: false,
      };
    }
    return {
      hasSdp: true,
      hasAudioMLine: /m=audio\s/i.test(sdp),
      candidateCount: (sdp.match(/^a=candidate:/gim) ?? []).length,
      hasFingerprint: /^a=fingerprint:/im.test(sdp),
      hasIceUfrag: /^a=ice-ufrag:/im.test(sdp),
      hasIcePwd: /^a=ice-pwd:/im.test(sdp),
      hasSetup: /^a=setup:/im.test(sdp),
      hasRtcpMux: /^a=rtcp-mux/im.test(sdp),
    };
  }

  setAudioElement(element: HTMLAudioElement | null) {
    this.audioElement = element;
  }

  /** SIP connect parameters for debugging (password included — browser console only). */
  getConnectLoggingContext(): SipConnectLoggingContext | null {
    const opts = this.lastConnectOptions;
    if (!opts) return null;
    const c = opts.credentials;
    return {
      sipWssUrl: opts.sipWssUrl,
      sipDomain: opts.sipDomain,
      username: c.username,
      authUsername: c.authUsername ?? null,
      displayName: c.displayName ?? null,
      credentialDomain: c.domain ?? null,
      password: c.password,
    };
  }

  // CALL-NAV: SIP registration entry point (required before inbound/outbound calls).
  async connectAndRegister(options: ConnectOptions) {
    if (this.connectAndRegisterPromise) {
      await this.connectAndRegisterPromise.catch(() => undefined);
      if (
        this.matchesConnectOptions(options) &&
        this.registrationState === "registered"
      ) {
        return;
      }
    }

    const run = this.performConnectAndRegister(options);
    this.connectAndRegisterPromise = run;
    try {
      await run;
    } finally {
      if (this.connectAndRegisterPromise === run) {
        this.connectAndRegisterPromise = null;
      }
    }
  }

  private matchesConnectOptions(options: ConnectOptions) {
    const current = this.lastConnectOptions;
    if (!current) return false;

    return (
      current.sipWssUrl === options.sipWssUrl &&
      current.sipDomain === options.sipDomain &&
      current.credentials.username === options.credentials.username &&
      current.credentials.password === options.credentials.password &&
      (current.credentials.authUsername ?? current.credentials.username) ===
        (options.credentials.authUsername ?? options.credentials.username) &&
      (current.credentials.domain ?? null) === (options.credentials.domain ?? null)
    );
  }

  private async performConnectAndRegister(options: ConnectOptions) {
    if (this.stopPromise) {
      await this.stopPromise;
    }

    this.lastConnectOptions = options;
    this.shouldReconnect = true;
    this.registerRequestCompleted = false;
    this.events.onRegistrationStatus?.("connecting");

    if (this.userAgent && this.matchesConnectOptions(options)) {
      const transport = this.getTransportState();
      if (transport === "Connected") {
        if (this.isOperationallyRegistered()) {
          this.syncRegistrationStatus();
          return;
        }

        await this.safeRegister().catch((error) => {
          if (!isRegisterInProgressError(error)) {
            this.events.onError?.(error as Error);
          }
        });
        this.syncRegistrationStatus();
        return;
      }
    }

    if (this.userAgent) {
      await this.stop();
      this.shouldReconnect = true;
    }

    const { sipWssUrl, sipDomain, credentials } = options;
    this.debug("connectAndRegister:start", {
      sipWssUrl,
      sipDomain,
      username: credentials.username,
    });
    const uri = UserAgent.makeURI(buildSipUri(credentials.username, sipDomain));
    if (!uri) {
      throw new Error("Invalid SIP URI for registration.");
    }
    const authUsername = credentials.authUsername ?? credentials.username;
    const userAgentOptions: UserAgentOptions = {
      uri,
      transportOptions: {
        server: sipWssUrl,
        traceSip: false,
        keepAliveInterval: 30,
        keepAliveDebounce: 10,
        connectionTimeout: 15,
      },
      authorizationUsername: authUsername,
      authorizationPassword: credentials.password,
      displayName: credentials.displayName ?? credentials.username,
      logBuiltinEnabled: false,
      logLevel: "error",
      instanceIdAlwaysAdded: false,
      allowLegacyNotifications: true,
      sessionDescriptionHandlerFactoryOptions: {
        constraints: { audio: true, video: false },
        peerConnectionConfiguration: {
          iceServers: resolveSipIceServers() ?? undefined,
        },
      },
    };
    this.debug("connectAndRegister:iceServers", {
      iceServersConfigured: (resolveSipIceServers() ?? []).length,
      iceServers: resolveSipIceServers() ?? [],
    });

    this.userAgent = new UserAgent(userAgentOptions);
    this.userAgent.delegate = {
      onInvite: (invitation) => this.handleInvite(invitation),
      onMessage: (message) => this.handleMessage(message),
      onNotify: (notification) => this.handleNotify(notification),
    };

    this.registerer = new Registerer(this.userAgent, {
      expires: 120,
      refreshFrequency: 85,
    });

    this.registerer.stateChange.addListener((state) => {
      this.debug("registerer:stateChange", { state: String(state) });
      if (state === RegistererState.Registered) {
        this.registerRequestCompleted = true;
        this.registerRetryAttempt = 0;
        this.clearRegisterRetryTimer();
      }
      if (
        state === RegistererState.Unregistered ||
        state === RegistererState.Terminated
      ) {
        this.registerRequestCompleted = false;
      }
      this.syncRegistrationStatus();
    });

    const registererWaitingChange = (
      this.registerer as unknown as {
        waitingChange?: { addListener: (cb: () => void) => void };
      }
    ).waitingChange;
    registererWaitingChange?.addListener(() => {
      this.syncRegistrationStatus();
    });

    const transport = this.userAgent.transport as unknown as {
      stateChange?: { addListener?: (cb: (state: string) => void) => void };
    };
    transport?.stateChange?.addListener?.((state) => {
      this.debug("transport:stateChange", { state });
      if (state === "Connected") {
        this.events.onTransportStateChange?.("connected");
        this.syncRegistrationStatus();
      }
      if (state === "Disconnected") {
        this.events.onTransportStateChange?.("disconnected");
        this.registerRequestCompleted = false;
        this.events.onRegistrationStatus?.("offline");
        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      }
    });

    await this.userAgent.start();
    this.debug("userAgent:start:ok");

    await this.safeRegister();

    this.debug("registerer:register:ok");
    this.syncRegistrationStatus();
  }

  private isTransportConnected() {
    const transport = this.userAgent?.transport as { state?: string } | undefined;
    return transport?.state === "Connected";
  }

  private isRegistererWaiting() {
    if (!this.registerer) return false;
    return (
      (this.registerer as unknown as { waiting?: boolean }).waiting === true
    );
  }

  /** True only after sip.js registerer reaches Registered (200 OK), not when INVITE is merely sent. */
  private isOperationallyRegistered() {
    if (!this.registerer) return false;
    return this.registerer.state === RegistererState.Registered;
  }

  private syncRegistrationStatus() {
    const transportState = this.getTransportState();
    const registererState = this.getRegistererState();
    const registererWaiting = this.isRegistererWaiting();
    let nextStatus: SipRegistrationStatus = "connecting";

    if (!this.registerer) {
      nextStatus = "offline";
      this.events.onRegistrationStatus?.(nextStatus);
      return;
    }

    if (registererWaiting) {
      nextStatus = "connecting";
      this.events.onRegistrationStatus?.(nextStatus);
      return;
    }

    if (this.isOperationallyRegistered()) {
      nextStatus = "registered";
      this.events.onRegistrationStatus?.(nextStatus);
      return;
    }

    if (this.registerer.state === RegistererState.Terminated) {
      nextStatus = "error";
      this.events.onRegistrationStatus?.(nextStatus);
      return;
    }

    if (this.registerer.state === RegistererState.Unregistered) {
      if (this.isTransportConnected() && this.shouldReconnect) {
        if (this.registerRetryAttempt >= 4) {
          this.clearRegisterRetryTimer();
          nextStatus = "error";
          this.events.onRegistrationStatus?.(nextStatus);
          this.events.onError?.(this.buildRegisterExhaustedError());
          return;
        }

        nextStatus = "connecting";
        this.events.onRegistrationStatus?.(nextStatus);
        this.scheduleRegisterRetry();
        return;
      }

      nextStatus = "offline";
      this.events.onRegistrationStatus?.(nextStatus);
      return;
    }

    this.events.onRegistrationStatus?.(nextStatus);
  }

  canAttemptRegister() {
    if (this.connectAndRegisterPromise || this.registerInFlightPromise) {
      return false;
    }

    if (
      this.isTransportConnected() &&
      this.registerer?.state === RegistererState.Unregistered
    ) {
      return true;
    }

    const state = this.registrationState;
    return state === "offline" || state === "error";
  }

  private clearRegisterRetryTimer() {
    if (this.registerRetryTimer == null) return;
    window.clearTimeout(this.registerRetryTimer);
    this.registerRetryTimer = null;
  }

  private scheduleRegisterRetry() {
    if (!this.shouldReconnect) return;
    if (this.registerRetryAttempt >= 4) return;
    if (!this.registerer || !this.isTransportConnected()) return;
    if (this.isOperationallyRegistered()) return;
    if (this.isRegistererWaiting()) return;
    if (this.connectAndRegisterPromise || this.registerInFlightPromise) return;
    if (this.registerRetryTimer != null) return;

    const attempt = this.registerRetryAttempt;
    const delayMs = Math.min(2000 * 2 ** attempt, 15000);
    this.registerRetryTimer = window.setTimeout(() => {
      this.registerRetryTimer = null;
      void this.attemptRegisterWhenTransportReady();
    }, delayMs);
  }

  private async attemptRegisterWhenTransportReady() {
    if (!this.shouldReconnect || !this.registerer || !this.isTransportConnected()) {
      return;
    }

    if (this.registerRetryAttempt >= 4) {
      return;
    }

    if (this.isOperationallyRegistered() || this.isRegistererWaiting()) {
      return;
    }

    this.registerRetryAttempt += 1;
    this.events.onRegistrationStatus?.("connecting");

    try {
      await this.safeRegister();
      if (this.isOperationallyRegistered()) {
        this.registerRetryAttempt = 0;
      } else if (!this.isRegistererWaiting()) {
        this.scheduleRegisterRetry();
      }
    } catch (error) {
      if (!isRegisterInProgressError(error)) {
        this.events.onError?.(error as Error);
      }
      this.scheduleRegisterRetry();
    }

    this.syncRegistrationStatus();
  }

  private async safeRegister() {
    if (!this.registerer) return;
    if (this.registerer.state === RegistererState.Registered) return;

    if (this.registerInFlightPromise) {
      await this.registerInFlightPromise;
      return;
    }

    const run = (async () => {
      await this.registerer!.register({
        requestDelegate: {
          onAccept: () => {
            this.lastRegisterRejectStatusCode = null;
            this.lastRegisterRejectReasonPhrase = null;
          },
          onReject: (response) => {
            this.lastRegisterRejectStatusCode = response.message.statusCode ?? null;
            this.lastRegisterRejectReasonPhrase = response.message.reasonPhrase ?? null;
          },
        },
      });
    })();
    this.registerInFlightPromise = run;

    try {
      await run;
    } catch (error) {
      if (isRegisterInProgressError(error)) {
        if (this.isOperationallyRegistered()) {
          return;
        }
      }
      throw error;
    } finally {
      if (this.registerInFlightPromise === run) {
        this.registerInFlightPromise = null;
      }
    }
  }

  disconnectTransportSync() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const userAgent = this.userAgent;
    if (!userAgent) return;

    try {
      const transport = userAgent.transport as { disconnect?: () => void };
      transport.disconnect?.();
    } catch {
      // Page may be unloading — best-effort close only.
    }
  }

  async stop() {
    if (this.stopPromise) {
      await this.stopPromise;
      return;
    }

    this.stopPromise = this.stopInternal();
    try {
      await this.stopPromise;
    } finally {
      this.stopPromise = null;
    }
  }

  private async stopInternal() {
    this.shouldReconnect = false;
    this.clearRegisterRetryTimer();
    this.registerRetryAttempt = 0;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearSession();
    this.clearAllPendingInvitations();
    if (this.registerer) {
      try {
        await this.registerer.unregister();
      } catch {
        // Ignore cleanup failures.
      }
    }
    if (this.userAgent) {
      try {
        await this.userAgent.stop();
      } catch {
        // Ignore cleanup failures.
      }
    }
    this.userAgent = null;
    this.registerer = null;
    this.registerInFlightPromise = null;
    this.registerRequestCompleted = false;
    this.events.onRegistrationStatus?.("offline");
  }

  // CALL-NAV: Outbound SIP INVITE entry point.
  async call(target: string, sipDomain: string) {
    if (!this.userAgent) {
      throw new Error("SIP is not initialized.");
    }

    if (this.registerInFlightPromise) {
      await this.registerInFlightPromise.catch(() => undefined);
    }

    if (this.registrationState !== "registered") {
      await this.safeRegister().catch(() => undefined);
    }

    if (this.registrationState !== "registered") {
      throw new Error("Phone is not registered.");
    }

    const targetUri = UserAgent.makeURI(buildSipUri(target, sipDomain));
    if (!targetUri) {
      throw new Error("Invalid SIP target.");
    }
    this.debug("outgoing:call:target", {
      target,
      sipDomain,
      targetUri: targetUri.toString(),
    });

    if (
      this.heldSession &&
      this.session &&
      (this.session.state === SessionState.Establishing ||
        this.session.state === SessionState.Established)
    ) {
      throw new Error("Already in a call.");
    }

    if (
      this.heldSession &&
      (!this.session || this.session.state === SessionState.Terminated)
    ) {
      this.clearForegroundSession();
      this.restoreHeldSessionToForeground();
    }

    if (this.session) {
      const foreground = this.session;
      if (
        foreground.state === SessionState.Established &&
        this.isHeldSessionOnHold
      ) {
        this.heldSession = foreground;
        this.session = null;
      } else if (
        foreground.state === SessionState.Establishing ||
        foreground.state === SessionState.Established
      ) {
        throw new Error("Place the current call on hold before dialing out.");
      } else {
        throw new Error("Already in a call.");
      }
    }

    this.inviter = new Inviter(this.userAgent, targetUri, {
      earlyMedia: true,
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
      sessionDescriptionHandlerModifiers: [this.audioCodecModifier],
      sessionDescriptionHandlerModifiersReInvite: [this.audioCodecModifier],
    });
    const rejection = {
      statusCode: undefined as number | undefined,
      reasonPhrase: undefined as string | undefined,
    };
    let settleEstablished:
      | ((value: void | PromiseLike<void>) => void)
      | null = null;
    let rejectEstablished: ((error: Error) => void) | null = null;

    this.session = this.inviter;
    this.events.onCallStateChange?.("dialing", this.session, "outgoing");
    this.bindSessionEvents(this.session, "outgoing");
    const session = this.session;
    const rejectOutboundCall = (error: Error) => {
      if (!rejectEstablished) return;
      const reject = rejectEstablished;
      settleEstablished = null;
      rejectEstablished = null;
      reject(error);
    };
    const established = new Promise<void>((resolve, reject) => {
      if (!session) {
        reject(new Error("Call session not available."));
        return;
      }
      settleEstablished = resolve;
      rejectEstablished = reject;
      const listener = (state: SessionState) => {
        if (state === SessionState.Established) {
          session.stateChange.removeListener(listener);
          settleEstablished?.();
          settleEstablished = null;
          rejectEstablished = null;
        }
        if (state === SessionState.Terminated) {
          session.stateChange.removeListener(listener);
          queueMicrotask(() => {
            if (!rejectEstablished) return;
            const statusText = rejection.statusCode
              ? `SIP call rejected (${rejection.statusCode}${
                  rejection.reasonPhrase ? ` ${rejection.reasonPhrase}` : ""
                })`
              : "Call was terminated before being established.";
            const error = new Error(statusText) as SipCallError;
            error.statusCode = rejection.statusCode;
            error.reasonPhrase = rejection.reasonPhrase;
            error.target = target;
            rejectOutboundCall(error);
          });
        }
      };
      session.stateChange.addListener(listener);
    });

    try {
      await this.inviter.invite({
        sessionDescriptionHandlerModifiers: [this.audioCodecModifier],
        requestDelegate: {
          onProgress: (response) => {
            if (
              response.message.statusCode === 183 &&
              this.session === session
            ) {
              this.attachRemoteAudio(session);
            }
          },
          onReject: (response) => {
            rejection.statusCode = response.message.statusCode;
            rejection.reasonPhrase = response.message.reasonPhrase;
            const error = new Error(
              `SIP call rejected (${rejection.statusCode ?? "unknown"}${
                rejection.reasonPhrase ? ` ${rejection.reasonPhrase}` : ""
              })`
            ) as SipCallError;
            error.statusCode = rejection.statusCode;
            error.reasonPhrase = rejection.reasonPhrase;
            error.target = target;
            this.debug("outgoing:invite:rejected", {
              target,
              statusCode: rejection.statusCode ?? null,
              reasonPhrase: rejection.reasonPhrase ?? null,
            });
            rejectOutboundCall(error);
          },
        },
      });
      await established;
    } catch (error) {
      this.debug("outgoing:invite:error", {
        target,
        message: (error as Error)?.message ?? "unknown",
      });
      if (this.heldSession) {
        this.clearForegroundSession();
        this.restoreHeldSessionToForeground();
      }
      throw error;
    }
  }

  isForegroundSessionOnHold() {
    return (
      this.session != null &&
      this.session.state === SessionState.Established &&
      this.isHeldSessionOnHold
    );
  }

  hasHeldBackgroundSession() {
    return (
      this.heldSession != null &&
      (this.heldSession.state === SessionState.Established ||
        this.heldSession.state === SessionState.Establishing)
    );
  }

  isForegroundOutbound() {
    return this.inviter != null && this.session === this.inviter;
  }

  // CALL-NAV: Incoming SIP accept path (answers matching pending INVITE).
  async answer(options?: string | SipAnswerOptions) {
    const normalized: SipAnswerOptions =
      typeof options === "string" ? { incomingId: options } : (options ?? {});
    const { incomingId, superviseMode } = normalized;
    const entry = this.findPendingInvitationEntry(incomingId);
    if (!entry) {
      throw new Error("No incoming SIP invitation to answer.");
    }
    const { invitation } = entry;
    const listenOnly = superviseMode === "listen";
    const bargeMode = superviseMode === "barge";
    this.debug("answer:attempt", {
      hasInvitation: true,
      sessionState: String(invitation.state),
      incomingId: incomingId ?? null,
      superviseMode: superviseMode ?? null,
    });
    this.session = invitation;
    this.pendingAnswerSuperviseMode = superviseMode ?? null;
    const directionModifiers = listenOnly
      ? [this.audioCodecModifier, this.listenDirectionModifier]
      : bargeMode
        ? [this.audioCodecModifier, this.bargeDirectionModifier]
        : [this.audioCodecModifier];
    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: !listenOnly, video: false },
      },
      sessionDescriptionHandlerModifiers: directionModifiers,
    });
    if (bargeMode) {
      this.setAudioEnabled(true);
    }
    this.debug("answer:accepted", { superviseMode: superviseMode ?? null });
  }

  hasIncomingInvitation() {
    return this.pendingInvitations.size > 0;
  }

  get registrationState(): SipRegistrationStatus {
    if (!this.registerer) return "offline";
    if (this.isRegistererWaiting()) return "connecting";
    if (this.isOperationallyRegistered()) return "registered";
    switch (this.registerer.state) {
      case RegistererState.Unregistered:
        return "offline";
      case RegistererState.Terminated:
        return "error";
      default:
        return "connecting";
    }
  }

  getTransportState() {
    const transport = this.userAgent?.transport as { state?: string } | undefined;
    return transport?.state ?? null;
  }

  getRegistererState() {
    if (!this.registerer) return null;
    return String(this.registerer.state);
  }

  getLastRegisterReject() {
    return {
      statusCode: this.lastRegisterRejectStatusCode,
      reasonPhrase: this.lastRegisterRejectReasonPhrase,
    };
  }

  private buildRegisterExhaustedError() {
    const extension = this.lastConnectOptions?.credentials.username ?? "extension";
    const statusCode = this.lastRegisterRejectStatusCode;
    const reasonPhrase = this.lastRegisterRejectReasonPhrase;

    if (statusCode === 503) {
      return new Error(
        `SIP REGISTER rejected with 503 Service Unavailable. Asterisk received the request but refused registration for ${extension} — check endpoint transport-wss, auth, and identify configuration.`
      );
    }

    if (statusCode === 401 || statusCode === 403) {
      return new Error(
        `SIP REGISTER rejected with ${statusCode}${reasonPhrase ? ` ${reasonPhrase}` : ""}. Check extension password and auth username.`
      );
    }

    if (statusCode === 408) {
      return new Error(
        `SIP REGISTER timed out (408). No response from server — check network path to ${this.lastConnectOptions?.sipWssUrl ?? "WSS"}.`
      );
    }

    return new Error(
      "SIP REGISTER failed repeatedly. Check WSS URL and Asterisk endpoint configuration."
    );
  }

  async register() {
    if (this.connectAndRegisterPromise) {
      await this.connectAndRegisterPromise;
      return;
    }

    if (!this.registerer) {
      if (this.lastConnectOptions) {
        await this.connectAndRegister(this.lastConnectOptions);
      }
      return;
    }
    if (this.registerer.state === RegistererState.Registered) return;
    if (!this.canAttemptRegister()) return;

    this.events.onRegistrationStatus?.("connecting");
    await this.safeRegister();
    this.syncRegistrationStatus();
  }

  hasPendingInvitation(incomingId?: string): boolean {
    return this.findPendingInvitationEntry(incomingId) !== null;
  }

  async rejectPendingInvitation(incomingId?: string) {
    const entry = this.findPendingInvitationEntry(incomingId);
    if (!entry) return;
    const { invitation } = entry;
    if (invitation.state !== SessionState.Initial) return;
    try {
      await invitation.reject();
    } catch (error) {
      this.events.onError?.(error as Error);
    }
  }

  private buildInvitationCorrelators(invitation: Invitation) {
    const fromUri = invitation.remoteIdentity.uri?.toString?.() ?? null;
    const payload = this.buildIncomingPayload({
      callId: invitation.request.callId,
      fromUri,
      displayName: invitation.remoteIdentity.displayName,
      body: invitation.request.body,
      getHeader: (name: string) => invitation.request.getHeader(name),
    });
    const correlatorIds = new Set<string>();
    const candidates = [
      invitation.request.callId,
      payload?.id,
      payload?.callUuid,
      payload?.callId,
      (invitation as { id?: string }).id,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        correlatorIds.add(candidate.trim());
      }
    }
    return correlatorIds;
  }

  private findPendingInvitationEntry(
    incomingId?: string
  ): PendingInvitationEntry | null {
    if (this.pendingInvitations.size === 0) return null;
    if (!incomingId) {
      if (this.pendingInvitations.size === 1) {
        return [...this.pendingInvitations.values()][0] ?? null;
      }
      return null;
    }

    for (const entry of this.pendingInvitations.values()) {
      if (entry.correlatorIds.has(incomingId)) return entry;
      if (entry.invitation.request.callId === incomingId) return entry;
      const sessionId = (entry.invitation as { id?: string }).id;
      if (sessionId === incomingId) return entry;
    }

    if (this.pendingInvitations.size === 1) {
      return [...this.pendingInvitations.values()][0] ?? null;
    }

    return null;
  }

  private removePendingInvitation(sipCallId: string) {
    const entry = this.pendingInvitations.get(sipCallId);
    if (!entry) return;
    this.pendingInvitations.delete(sipCallId);
    this.events.onInvitationEnded?.([...entry.correlatorIds]);
    if (
      this.pendingInvitations.size === 0 &&
      (!this.session ||
        this.session.state === SessionState.Terminated ||
        this.session === entry.invitation)
    ) {
      if (this.session === entry.invitation) {
        this.session = null;
      }
      if (!this.inviter && !this.isActiveEstablishedSession()) {
        this.events.onCallStateChange?.("idle", null, "incoming");
      }
    }
  }

  private isActiveEstablishedSession() {
    return (
      this.session != null &&
      (this.session.state === SessionState.Established ||
        this.session.state === SessionState.Establishing)
    );
  }

  private resolveSessionDirection(session: Session): "incoming" | "outgoing" {
    return (
      this.sessionDirections.get(session) ??
      (this.inviter === session ? "outgoing" : "incoming")
    );
  }

  private async terminateSession(session: Session) {
    if (
      this.inviter === session &&
      (session.state === SessionState.Initial ||
        session.state === SessionState.Establishing)
    ) {
      await this.inviter.cancel();
      return;
    }

    if (session.state === SessionState.Initial) {
      const pendingEntry = [...this.pendingInvitations.values()].find(
        (entry) => entry.invitation === session
      );
      if (pendingEntry) {
        await pendingEntry.invitation.reject();
      }
      return;
    }

    if (session.state === SessionState.Established) {
      await session.bye();
      return;
    }

    if (session.state !== SessionState.Terminated) {
      await session.dispose();
    }
  }

  // CALL-NAV: Shared hangup path for active incoming/outgoing SIP sessions.
  async hangup() {
    const session = this.session;
    if (!session) return;

    try {
      await this.terminateSession(session);
    } catch (error) {
      this.events.onError?.(error as Error);
    } finally {
      this.releaseLocalAudioTracks();

      const heldSession = this.heldSession;
      const shouldRestoreHeldInbound =
        heldSession != null &&
        this.session === session &&
        session !== heldSession &&
        this.resolveSessionDirection(heldSession) === "incoming";

      if (shouldRestoreHeldInbound) {
        this.clearForegroundSession();
        this.restoreHeldSessionToForeground();
        this.events.onCallStateChange?.("held", this.session, "incoming");
        return;
      }

      if (
        heldSession != null &&
        this.session === session &&
        session !== heldSession &&
        this.resolveSessionDirection(heldSession) === "outgoing"
      ) {
        this.heldSession = null;
        try {
          await this.terminateSession(heldSession);
        } catch (error) {
          this.events.onError?.(error as Error);
        }
      }

      // Session listener already restored the held inbound leg after outbound ended.
      if (
        this.session != null &&
        this.session !== session &&
        (this.session.state === SessionState.Established ||
          this.session.state === SessionState.Establishing)
      ) {
        return;
      }

      this.clearHeldSession();
      this.clearSession();
      this.events.onCallStateChange?.("ended", null);
    }
  }

  mute() {
    this.setAudioEnabled(false);
  }

  unmute() {
    this.setAudioEnabled(true);
  }

  async hold() {
    await this.setHoldState(true);
    this.isHeldSessionOnHold = true;
  }

  async unhold() {
    await this.setHoldState(false);
    this.isHeldSessionOnHold = false;
  }

  canSwapSessions() {
    return (
      this.session?.state === SessionState.Established &&
      this.heldSession?.state === SessionState.Established
    );
  }

  async swapSessions(): Promise<{
    foregroundDirection: "incoming" | "outgoing";
    backgroundDirection: "incoming" | "outgoing";
  }> {
    const foreground = this.session;
    const background = this.heldSession;
    if (!foreground || !background) {
      throw new Error("Both call legs must be active to swap.");
    }
    if (
      foreground.state !== SessionState.Established ||
      background.state !== SessionState.Established
    ) {
      throw new Error("Both call legs must be established to swap.");
    }

    const foregroundDirection =
      this.sessionDirections.get(foreground) ??
      (this.inviter === foreground ? "outgoing" : "incoming");
    const backgroundDirection =
      this.sessionDirections.get(background) ??
      (this.inviter === background ? "outgoing" : "incoming");
  const outboundWasBackground = this.inviter != null && this.inviter === background;

    await foreground.invite({
      sessionDescriptionHandlerModifiers: [
        this.audioCodecModifier,
        holdModifier,
      ],
    });

    await background.invite({
      sessionDescriptionHandlerModifiers: [this.audioCodecModifier],
    });

    this.session = background;
    this.heldSession = foreground;
    this.isHeldSessionOnHold = true;

    if (outboundWasBackground && this.inviter != null) {
      this.inviter = this.session as Inviter;
    }

    this.attachRemoteAudio(this.session);

    const newForegroundDirection = backgroundDirection;
    const newBackgroundDirection = foregroundDirection;

    this.events.onSessionsSwapped?.({
      foregroundDirection: newForegroundDirection,
      backgroundDirection: newBackgroundDirection,
    });
    this.events.onCallStateChange?.(
      "active",
      this.session,
      newForegroundDirection
    );

    return {
      foregroundDirection: newForegroundDirection,
      backgroundDirection: newBackgroundDirection,
    };
  }

  async sendDtmf(tone: string) {
    const normalizedTone = tone.trim().toUpperCase();
    if (!dtmfToneRegex.test(normalizedTone)) {
      throw new Error("Invalid DTMF tone.");
    }
    const session = this.session;
    if (!session || session.state !== SessionState.Established) {
      throw new Error("No active call session for DTMF.");
    }

    // Prefer RTP telephone-event so tones are signaled in-band without local
    // speaker playback artifacts from synthetic keypad audio.
    const pc = this.getPeerConnection();
    if (pc) {
      for (const sender of pc.getSenders()) {
        if (sender.track?.kind !== "audio" || !sender.dtmf) continue;
        if (!sender.dtmf.canInsertDTMF) continue;
        try {
          sender.dtmf.insertDTMF(normalizedTone, 120, 80);
          return;
        } catch {
          // Fall through to SIP INFO relay when RTP DTMF is unavailable.
        }
      }
    }

    const info = (
      session as unknown as {
        info?: (options: {
          requestOptions?: { extraHeaders?: string[] };
          body: { content: string; contentType: string };
        }) => Promise<void>;
      }
    ).info;

    if (typeof info === "function") {
      await info.call(session, {
        body: {
          content: `Signal=${normalizedTone}\r\nDuration=120`,
          contentType: "application/dtmf-relay",
        },
      });
      return;
    }

    throw new Error("DTMF is not supported for the current call session.");
  }

  async transferBlind(target: string, sipDomain: string) {
    const session = this.session;
    if (!session) return;
    const targetUri = UserAgent.makeURI(buildSipUri(target, sipDomain));
    if (!targetUri) {
      throw new Error("Invalid SIP transfer target.");
    }
    const refer = (
      session as unknown as { refer?: (uri: any) => Promise<void> }
    ).refer;
    if (typeof refer === "function") {
      await refer.call(session, targetUri);
    } else {
      throw new Error("SIP transfer is not supported in this session.");
    }
  }

  async transferCurrentCallToRoom(room: string, sipDomain: string) {
    await this.transferBlind(room, sipDomain);
  }

  async joinRoom(room: string, sipDomain: string) {
    const released = await this.waitForSessionRelease();
    if (!released) {
      // Room transfers can leave a stale SIP leg attached; dispose it so the
      // operator can immediately rejoin and retain control of the customer call.
      try {
        await this.session?.dispose();
      } catch {
        // Best-effort cleanup; proceed with room join.
      } finally {
        this.clearSession();
      }
    }
    await this.call(room, sipDomain);
  }

  private async waitForSessionRelease(timeoutMs = 4_000) {
    const session = this.session;
    if (!session) return true;

    if (session.state === SessionState.Terminated) {
      this.clearSession();
      return true;
    }

    return await new Promise<boolean>((resolve) => {
      const listener = (state: SessionState) => {
        if (state !== SessionState.Terminated) return;
        session.stateChange.removeListener(listener);
        window.clearTimeout(timer);
        this.clearSession();
        resolve(true);
      };

      const timer = window.setTimeout(() => {
        session.stateChange.removeListener(listener);
        resolve(false);
      }, timeoutMs);

      session.stateChange.addListener(listener);
    });
  }

  // CALL-NAV: Primary inbound SIP INVITE handler.
  private handleInvite(invitation: Invitation) {
    this.debug("handleInvite:received", {
      callId: invitation.request.callId,
      fromUri: invitation.remoteIdentity.uri?.toString?.() ?? null,
      displayName: invitation.remoteIdentity.displayName ?? null,
    });
    const currentSession = this.session;
    const heldBackgroundBusy =
      this.heldSession != null &&
      (this.heldSession.state === SessionState.Establishing ||
        this.heldSession.state === SessionState.Established);

    if (heldBackgroundBusy) {
      this.debug("handleInvite:reject:busy", {
        reason: "held-background-session",
        nextCallId: invitation.request.callId,
      });
      void invitation.reject().catch(() => {});
      return;
    }

    if (currentSession && currentSession !== invitation) {
      if (
        currentSession.state === SessionState.Establishing ||
        currentSession.state === SessionState.Established
      ) {
        this.debug("handleInvite:reject:busy", {
          currentState: String(currentSession.state),
          currentCallId: (currentSession as { request?: { callId?: string } })?.request?.callId ?? null,
          nextCallId: invitation.request.callId,
        });
        void invitation.reject().catch(() => {});
        return;
      }

      if (
        this.inviter === currentSession &&
        currentSession.state === SessionState.Initial
      ) {
        void this.inviter.cancel().catch(() => {});
        this.clearSession();
      }
    } else if (
      currentSession &&
      currentSession.state === SessionState.Terminated
    ) {
      this.clearSession();
    }

    const sipCallId = invitation.request.callId;
    const correlatorIds = this.buildInvitationCorrelators(invitation);
    this.pendingInvitations.set(sipCallId, { invitation, correlatorIds });
    if (!this.isActiveEstablishedSession()) {
      this.session = invitation;
    }

    const fromUri = invitation.remoteIdentity.uri?.toString?.() ?? null;
    const incomingPayload = this.buildIncomingPayload({
      callId: invitation.request.callId,
      fromUri,
      displayName: invitation.remoteIdentity.displayName,
      body: invitation.request.body,
      getHeader: (name: string) => invitation.request.getHeader(name),
    });
    if (incomingPayload) {
      this.debug("handleInvite:payload", {
        id: incomingPayload.id,
        callUuid: incomingPayload.callUuid ?? null,
        customerId: incomingPayload.customerId ?? null,
        callerNumber: incomingPayload.callerNumber ?? null,
        customerName: incomingPayload.customerName ?? null,
        service: incomingPayload.service ?? null,
      });
      this.events.onIncomingCallPayload?.(incomingPayload, "sip-invite");
    } else {
      this.debug("handleInvite:fallbackIncoming", { fromUri });
      this.events.onIncomingCall?.(fromUri);
    }
    this.events.onCallStateChange?.("ringing", invitation, "incoming");
    this.bindSessionEvents(invitation, "incoming", sipCallId);
  }

  // CALL-NAV: Inbound metadata fallback via SIP MESSAGE.
  private handleMessage(message: Message) {
    const request = message.request;
    const fromUri = request.from?.uri?.toString?.() ?? null;
    const payload = this.buildIncomingPayload({
      callId: request.callId,
      fromUri,
      displayName: request.from?.displayName,
      body: request.body,
      getHeader: (name: string) => request.getHeader(name),
    });

    if (payload) {
      console.log("[IncomingCall][SIP message]", {
        callId: request.callId ?? null,
        fromUri,
        incomingMessage: request.body ?? null,
        payload,
      });
      this.events.onIncomingCallPayload?.(payload, "sip-message");
    }

    void message.accept().catch(() => {});
  }

  // CALL-NAV: Inbound metadata fallback via SIP NOTIFY.
  private handleNotify(notification: Notification) {
    const request = notification.request;
    const fromUri = request.from?.uri?.toString?.() ?? null;
    const payload = this.buildIncomingPayload({
      callId: request.callId,
      fromUri,
      displayName: request.from?.displayName,
      body: request.body,
      getHeader: (name: string) => request.getHeader(name),
    });

    if (payload) {
      this.events.onIncomingCallPayload?.(payload, "sip-notify");
    }

    void notification.accept().catch(() => {});
  }

  private asObject(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return null;
    }
    return value as Record<string, unknown>;
  }

  private asString(value: unknown) {
    if (typeof value === "string") {
      const normalized = value.trim();
      return normalized.length > 0 ? normalized : undefined;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private asBoolean(value: unknown) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number" && Number.isFinite(value)) {
      if (value === 1) return true;
      if (value === 0) return false;
      return undefined;
    }
    if (typeof value !== "string") return undefined;
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "y", "1", "on"].includes(normalized)) return true;
    if (["false", "no", "n", "0", "off"].includes(normalized)) return false;
    return undefined;
  }

  private parseJsonBody(body?: string) {
    if (!body) return null;
    try {
      return this.asObject(JSON.parse(body));
    } catch {
      return null;
    }
  }

  private parseSipUser(value?: string | null) {
    if (!value) return undefined;
    const sipMatch = value.match(/^sip:([^@;>]+)/i);
    if (sipMatch?.[1]) return sipMatch[1];
    const angleMatch = value.match(/<sip:([^@;>]+)/i);
    if (angleMatch?.[1]) return angleMatch[1];
    return undefined;
  }

  private normalizeBodyKey(value: string) {
    return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  private getBodyKeyLookup(source: Record<string, unknown>) {
    const cached = this.bodyKeyLookupCache.get(source);
    if (cached) return cached;

    const lookup = new Map<string, unknown>();
    for (const [key, value] of Object.entries(source)) {
      const normalized = this.normalizeBodyKey(key);
      if (!normalized || lookup.has(normalized)) continue;
      lookup.set(normalized, value);
    }

    this.bodyKeyLookupCache.set(source, lookup);
    return lookup;
  }

  private readBodyValue(source: Record<string, unknown>, key: string) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }

    const normalized = this.normalizeBodyKey(key);
    if (!normalized) return undefined;

    return this.getBodyKeyLookup(source).get(normalized);
  }

  private firstString(
    source: Record<string, unknown> | null,
    keys: readonly string[]
  ) {
    if (!source) return undefined;
    for (const key of keys) {
      const value = this.asString(this.readBodyValue(source, key));
      if (value) return value;
    }
    return undefined;
  }

  private firstBoolean(
    source: Record<string, unknown> | null,
    keys: readonly string[]
  ) {
    if (!source) return undefined;
    for (const key of keys) {
      const value = this.asBoolean(this.readBodyValue(source, key));
      if (typeof value === "boolean") return value;
    }
    return undefined;
  }

  private firstHeader(
    getHeader: ((name: string) => string | undefined) | undefined,
    names: readonly string[]
  ) {
    if (!getHeader) return undefined;
    for (const name of names) {
      const value = this.asString(getHeader(name));
      if (value) return value;
    }
    return undefined;
  }

  private buildIncomingPayload(input: {
    callId?: string;
    fromUri?: string | null;
    displayName?: string | null;
    body?: string;
    getHeader?: (name: string) => string | undefined;
  }): CallIncomingPayload | null {
    const parsedBody = this.parseJsonBody(input.body);
    const id = this.firstString(parsedBody, [
      "id",
      "uniqueId",
      "unique_id",
      "uniqueid",
      "callId",
      "call_id",
      "sessionId",
      "session_id",
      "channelId",
      "channel_id",
    ]);
    const callerNumber =
      this.firstString(parsedBody, [
        "callerNumber",
        "caller_number",
        "callerId",
        "caller_id",
        "callerIdNum",
        "caller_id_num",
        "phoneNumber",
        "phone_number",
        "from",
        "fromNumber",
        "ani",
        "msisdn",
      ]) ??
      this.firstHeader(input.getHeader, [
        "x-caller-number",
        "x-caller-id",
        "x-from-number",
      ]) ??
      this.parseSipUser(input.fromUri);
    const customerId =
      this.firstString(parsedBody, [
        "customerId",
        "customer_id",
        "customerUuid",
        "customer_uuid",
        "clientId",
        "client_id",
        "subscriberId",
        "subscriber_id",
        "memberId",
        "member_id",
        "accountId",
        "account_id",
      ]) ?? this.firstHeader(input.getHeader, ["x-customer-id", "x-customer-uuid"]);

    const customerName =
      this.firstString(parsedBody, [
        "customerName",
        "customer_name",
        "callerName",
        "caller_name",
        "callerIdName",
        "caller_id_name",
        "name",
      ]) ?? this.asString(input.displayName ?? undefined);

    const callId =
      this.firstString(parsedBody, [
        "callId",
        "call_id",
        "callid",
      ]) ?? this.firstHeader(input.getHeader, ["x-call-id"]);

    const callUuid =
      this.firstString(parsedBody, [
        "callUuid",
        "call_uuid",
        "uniqueId",
        "unique_id",
        "uniqueid",
        "linkedId",
        "linked_id",
        "linkedid",
        "uuid",
      ]) ??
      this.firstHeader(input.getHeader, ["x-call-uuid"]);

    const service =
      this.firstString(parsedBody, [
        "service",
        "callReference",
        "call_reference",
        "reference",
        "queue",
        "queueName",
        "queue_name",
        "context",
      ]) ?? this.firstHeader(input.getHeader, ["x-service", "x-queue"]);

    const description = this.firstString(parsedBody, [
      "description",
      "reason",
      "type",
      "event",
      "eventType",
      "event_type",
      "subEvent",
      "sub_event",
      "channelStateDesc",
      "channel_state_desc",
    ]);

    const signalType = this.firstString(parsedBody, [
      "signalType",
      "signal_type",
      "eventType",
      "event_type",
      "type",
      "subEvent",
      "sub_event",
    ]);

    const deviceCid =
      this.firstString(parsedBody, ["deviceCid", "device_cid"]) ??
      this.firstHeader(input.getHeader, ["x-device-cid"]);

    const informationalFlag = this.firstBoolean(parsedBody, [
      "isInformational",
      "is_informational",
      "informational",
      "infoOnly",
      "info_only",
      "noAnswerRequired",
      "no_answer_required",
    ]);

    const requiresAnswerFromPayload =
      this.firstBoolean(parsedBody, [
        "requiresAnswer",
        "requires_answer",
        "answerRequired",
        "answer_required",
        "needsAnswer",
        "needs_answer",
      ]) ??
      this.asBoolean(
        this.firstHeader(input.getHeader, ["x-requires-answer", "x-answer-required"])
      );

    const requiresAnswer =
      typeof informationalFlag === "boolean"
        ? !informationalFlag
        : requiresAnswerFromPayload;

    const hasSignal =
      Boolean(id) ||
      Boolean(callUuid) ||
      Boolean(callerNumber) ||
      Boolean(customerName) ||
      Boolean(service) ||
      Boolean(description) ||
      Boolean(signalType) ||
      Boolean(deviceCid) ||
      typeof requiresAnswer === "boolean";

    if (!hasSignal) {
      return null;
    }

    return {
      id: callUuid ?? callId ?? id ?? this.asString(input.callId),
      customerId: customerId ?? undefined,
      callUuid,
      callId,
      callerNumber,
      customerName,
      service,
      description: description ?? "Voice Call",
      signalType,
      deviceCid,
      requiresAnswer,
      metadata: parsedBody ?? null,
      receivedAtMs: Date.now(),
    };
  }

  // CALL-NAV: Session state wiring shared by inbound/outbound calls.
  private bindSessionEvents(
    session: Session,
    direction: "incoming" | "outgoing",
    pendingSipCallId?: string
  ) {
    this.sessionDirections.set(session, direction);
    session.stateChange.addListener((state) => {
      this.debug("session:stateChange", {
        direction,
        state: String(state),
        pendingCount: this.pendingInvitations.size,
        hasInviter: Boolean(this.inviter),
      });
      if (state === SessionState.Established) {
        if (pendingSipCallId) {
          this.pendingInvitations.delete(pendingSipCallId);
        }

        const isHeldBackgroundLeg =
          this.heldSession === session &&
          this.session != null &&
          this.session !== session &&
          this.session.state !== SessionState.Terminated;

        if (!isHeldBackgroundLeg) {
          this.session = session;
          const superviseMode = this.pendingAnswerSuperviseMode;
          this.pendingAnswerSuperviseMode = null;
          if (superviseMode === "barge") {
            void this.ensureBargeOutboundAudio(session).catch((error) => {
              this.debug("ensureBargeOutboundAudio:error", {
                message: (error as Error)?.message ?? "unknown",
              });
            });
          }
          this.events.onCallStateChange?.("active", session, direction);
          this.attachRemoteAudio(session);
        }
      }
      if (state === SessionState.Terminated) {
        if (
          pendingSipCallId &&
          this.pendingInvitations.has(pendingSipCallId)
        ) {
          this.removePendingInvitation(pendingSipCallId);
          return;
        }

        if (this.heldSession === session) {
          this.clearHeldSession();
          if (
            this.session &&
            this.session.state !== SessionState.Terminated
          ) {
            this.events.onBackgroundHeldEnded?.();
            return;
          }
          if (!this.session || this.session.state === SessionState.Terminated) {
            this.events.onCallStateChange?.("ended", null, "incoming");
          }
          return;
        }

        if (this.session === session) {
          if (this.heldSession && direction === "outgoing") {
            this.clearForegroundSession();
            this.restoreHeldSessionToForeground();
            this.events.onCallStateChange?.("held", this.session, "incoming");
            return;
          }

          const heldSession = this.heldSession;
          if (
            heldSession &&
            direction === "incoming" &&
            this.resolveSessionDirection(heldSession) === "outgoing"
          ) {
            this.heldSession = null;
            void this.terminateSession(heldSession).catch((error) => {
              this.events.onError?.(error as Error);
            });
          }

          this.events.onCallStateChange?.("ended", session, direction);
          this.clearHeldSession();
          this.clearSession();
          if (this.pendingInvitations.size > 0 && !this.inviter) {
            this.events.onCallStateChange?.("ringing", null, "incoming");
          }
        }
      }
    });
  }

  private setAudioEnabled(enabled: boolean) {
    const pc = this.getPeerConnection();
    if (!pc) return;
    for (const sender of pc.getSenders()) {
      if (sender.track && sender.track.kind === "audio") {
        sender.track.enabled = enabled;
      }
    }
  }

  private async setHoldState(hold: boolean) {
    const session = this.resolveHoldTargetSession();
    if (!session) return;
    const options = hold
      ? {
          sessionDescriptionHandlerModifiers: [
            this.audioCodecModifier,
            holdModifier,
          ],
        }
      : { sessionDescriptionHandlerModifiers: [this.audioCodecModifier] };
    await session.invite(options);
  }

  private resolveAudioCodecKeys() {
    const configured = resolveSipAudioCodecs() ?? [];
    const source =
      configured.length > 0 ? configured : [...defaultAudioCodecKeys];
    const seen = new Set<string>();
    const codecKeys: string[] = [];
    for (const codec of source) {
      const key = normalizeCodecKey(codec);
      if (!key || key === telephoneEventCodecKey || seen.has(key)) {
        continue;
      }
      seen.add(key);
      codecKeys.push(key);
    }
    return codecKeys.length > 0 ? codecKeys : [...defaultAudioCodecKeys];
  }

  private buildAudioCodecModifier(): SessionDescriptionHandlerModifier {
    return async (description: RTCSessionDescriptionInit) => {
      if (!description.sdp || !description.type) {
        throw new Error("Invalid SDP");
      }
      return {
        ...description,
        sdp: this.prioritizeAudioCodecs(description.sdp),
      };
    };
  }

  private buildAudioDirectionModifier(
    direction: "sendrecv" | "recvonly"
  ): SessionDescriptionHandlerModifier {
    return async (description: RTCSessionDescriptionInit) => {
      if (!description.sdp || !description.type) {
        throw new Error("Invalid SDP");
      }
      return {
        ...description,
        sdp: this.rewriteAudioSectionDirection(description.sdp, direction),
      };
    };
  }

  private rewriteAudioSectionDirection(
    sdp: string,
    direction: "sendrecv" | "recvonly"
  ) {
    const lines = sdp.split(/\r\n/);
    for (let index = 0; index < lines.length; index += 1) {
      if (!lines[index]?.startsWith("m=audio ")) continue;

      let sectionEnd = index + 1;
      while (sectionEnd < lines.length && !lines[sectionEnd]?.startsWith("m=")) {
        sectionEnd += 1;
      }

      const section = lines.slice(index, sectionEnd).filter((line) => {
        return !/^a=(sendrecv|sendonly|recvonly|inactive)/.test(line);
      });
      section.splice(1, 0, `a=${direction}`);
      lines.splice(index, sectionEnd - index, ...section);
      index += section.length - 1;
    }
    return lines.join("\r\n");
  }

  private async ensureBargeOutboundAudio(session: Session) {
    const pc = this.getPeerConnectionFromSession(session);
    if (!pc) return;

    const existingSender = pc
      .getSenders()
      .find((sender) => sender.track?.kind === "audio");
    if (existingSender?.track) {
      existingSender.track.enabled = true;
      this.setAudioEnabled(true);
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });
    const track = stream.getAudioTracks()[0];
    if (!track) {
      throw new Error("No microphone track available for barge.");
    }

    if (existingSender) {
      await existingSender.replaceTrack(track);
    } else {
      pc.addTrack(track, stream);
    }

    const invite = (
      session as unknown as {
        invite?: (options?: {
          sessionDescriptionHandlerModifiers?: SessionDescriptionHandlerModifier[];
        }) => Promise<void>;
      }
    ).invite;
    if (typeof invite === "function") {
      await invite.call(session, {
        sessionDescriptionHandlerModifiers: [
          this.audioCodecModifier,
          this.bargeDirectionModifier,
        ],
      });
    }

    this.setAudioEnabled(true);
    this.debug("ensureBargeOutboundAudio:ok", {
      senders: pc.getSenders().length,
    });
  }

  private getPeerConnectionFromSession(session: Session) {
    const sdh = session.sessionDescriptionHandler as unknown as {
      peerConnection?: RTCPeerConnection;
    };
    return sdh?.peerConnection ?? null;
  }

  private releaseLocalAudioTracks() {
    const pc = this.getPeerConnection();
    if (!pc) return;
    for (const sender of pc.getSenders()) {
      const track = sender.track;
      if (track?.kind === "audio") {
        track.stop();
      }
    }
  }

  private prioritizeAudioCodecs(sdp: string) {
    const lines = sdp.split(/\r\n/);
    for (let index = 0; index < lines.length; index += 1) {
      if (!lines[index]?.startsWith("m=audio ")) continue;
      let sectionEnd = index + 1;
      while (sectionEnd < lines.length && !lines[sectionEnd]?.startsWith("m=")) {
        sectionEnd += 1;
      }
      const rewrittenSection = this.rewriteAudioSection(
        lines.slice(index, sectionEnd)
      );
      lines.splice(index, sectionEnd - index, ...rewrittenSection);
      index += rewrittenSection.length - 1;
    }
    return lines.join("\r\n");
  }

  private rewriteAudioSection(sectionLines: string[]) {
    const mLine = sectionLines[0];
    if (!mLine) return sectionLines;
    const mLineParts = mLine.trim().split(/\s+/);
    if (mLineParts.length < 4) return sectionLines;

    const originalPayloads = mLineParts.slice(3);
    const payloadCodecMap = new Map<string, string>();
    for (const line of sectionLines) {
      const rtpMapMatch = line.match(/^a=rtpmap:(\d+)\s+([^/\s]+)/i);
      if (!rtpMapMatch) continue;
      payloadCodecMap.set(rtpMapMatch[1], normalizeCodecKey(rtpMapMatch[2]));
    }

    const preferredCodecs = new Set(this.audioCodecKeys);
    const hasPreferredCodec = originalPayloads.some((payload) =>
      preferredCodecs.has(payloadCodecMap.get(payload) ?? "")
    );
    if (!hasPreferredCodec) {
      return sectionLines;
    }

    const allowedCodecs = new Set([
      ...this.audioCodecKeys,
      telephoneEventCodecKey,
    ]);
    const allowedPayloads = originalPayloads.filter((payload) => {
      const codec = payloadCodecMap.get(payload);
      return !codec || allowedCodecs.has(codec);
    });
    if (allowedPayloads.length === 0) {
      return sectionLines;
    }

    const disallowedPayloads = new Set(
      originalPayloads.filter((payload) => {
        const codec = payloadCodecMap.get(payload);
        return Boolean(codec && !allowedCodecs.has(codec));
      })
    );

    const orderedPayloads = this.uniqueValues([
      ...this.audioCodecKeys.flatMap((codec) =>
        allowedPayloads.filter((payload) => payloadCodecMap.get(payload) === codec)
      ),
      ...allowedPayloads.filter((payload) => !payloadCodecMap.has(payload)),
      ...allowedPayloads.filter(
        (payload) => payloadCodecMap.get(payload) === telephoneEventCodecKey
      ),
    ]);
    if (orderedPayloads.length === 0) {
      return sectionLines;
    }

    const filteredSectionLines = sectionLines.filter((line, lineIndex) => {
      if (lineIndex === 0) return true;
      const payloadLineMatch = line.match(
        /^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)\b/i
      );
      if (!payloadLineMatch) return true;
      return !disallowedPayloads.has(payloadLineMatch[1]);
    });

    filteredSectionLines[0] = `${mLineParts.slice(0, 3).join(" ")} ${orderedPayloads.join(" ")}`;
    return filteredSectionLines;
  }

  private uniqueValues(values: string[]) {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const value of values) {
      if (seen.has(value)) continue;
      seen.add(value);
      unique.push(value);
    }
    return unique;
  }

  private getPeerConnection() {
    const session = this.session as Session | null;
    const sdh = session?.sessionDescriptionHandler as unknown as {
      peerConnection?: RTCPeerConnection;
    };
    return sdh?.peerConnection ?? null;
  }

  private attachRemoteAudio(session: Session, attempt = 0) {
    if (!this.audioElement) return;
    if (this.heldSession === session) return;

    const pc = this.getPeerConnectionFromSession(session);
    if (!pc) {
      if (attempt < 20) {
        window.setTimeout(() => this.attachRemoteAudio(session, attempt + 1), 50);
      }
      return;
    }
    this.debug("attachRemoteAudio:start", {
      receivers: pc.getReceivers().length,
      senders: pc.getSenders().length,
      sessionState: String(session.state),
    });
    this.debug("pc:state:snapshot", {
      signalingState: pc.signalingState,
      iceConnectionState: pc.iceConnectionState,
      iceGatheringState: pc.iceGatheringState,
      connectionState: pc.connectionState,
    });
    this.debug("sdp:local:summary", this.summarizeSdp(pc.localDescription?.sdp));
    this.debug("sdp:remote:summary", this.summarizeSdp(pc.remoteDescription?.sdp));
    pc.oniceconnectionstatechange = () => {
      this.debug("pc:iceConnectionState", {
        state: pc.iceConnectionState,
      });
    };
    pc.onicegatheringstatechange = () => {
      this.debug("pc:iceGatheringState", {
        state: pc.iceGatheringState,
      });
    };
    pc.onconnectionstatechange = () => {
      this.debug("pc:connectionState", {
        state: pc.connectionState,
      });
    };
    pc.onsignalingstatechange = () => {
      this.debug("pc:signalingState", {
        state: pc.signalingState,
      });
    };
    pc.onicecandidateerror = (event) => {
      this.debug("pc:iceCandidateError", {
        address: event.address ?? null,
        port: event.port ?? null,
        url: event.url ?? null,
        errorCode: event.errorCode ?? null,
        errorText: event.errorText ?? null,
      });
    };
    this.logInboundAudioStats(pc);

    const remoteStream = new MediaStream();
    for (const receiver of pc.getReceivers()) {
      if (receiver.track) {
        this.debug("attachRemoteAudio:receiverTrack", {
          id: receiver.track.id,
          kind: receiver.track.kind,
          enabled: receiver.track.enabled,
          muted: receiver.track.muted,
          readyState: receiver.track.readyState,
        });
        remoteStream.addTrack(receiver.track);
        receiver.track.onmute = () => {
          this.debug("attachRemoteAudio:track:mute", { id: receiver.track?.id });
        };
        receiver.track.onunmute = () => {
          this.debug("attachRemoteAudio:track:unmute", { id: receiver.track?.id });
          void this.audioElement?.play().catch(() => {});
        };
        receiver.track.onended = () => {
          this.debug("attachRemoteAudio:track:ended", { id: receiver.track?.id });
        };
      }
    }

    pc.ontrack = (event) => {
      const tracksFromStreams = event.streams.flatMap((stream) => stream.getTracks());
      if (tracksFromStreams.length === 0 && event.track) {
        // Some browsers/SFU setups deliver track without stream references.
        tracksFromStreams.push(event.track);
      }
      this.debug("attachRemoteAudio:ontrack", {
        streamCount: event.streams.length,
        trackCount: tracksFromStreams.length,
        eventTrackKind: event.track?.kind ?? null,
        eventTrackId: event.track?.id ?? null,
      });
      for (const track of tracksFromStreams) {
        if (!remoteStream.getTracks().some((t) => t.id === track.id)) {
          remoteStream.addTrack(track);
          this.debug("attachRemoteAudio:ontrack:added", {
            id: track.id,
            kind: track.kind,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
          });
          track.onmute = () => {
            this.debug("attachRemoteAudio:track:mute", { id: track.id });
          };
          track.onunmute = () => {
            this.debug("attachRemoteAudio:track:unmute", { id: track.id });
            void this.audioElement?.play().catch(() => {});
          };
          track.onended = () => {
            this.debug("attachRemoteAudio:track:ended", { id: track.id });
          };
        }
      }
      void this.audioElement?.play().catch((error) => {
        this.debug("attachRemoteAudio:ontrack:play:error", {
          message: (error as Error)?.message ?? "unknown",
        });
      });
    };

    this.audioElement.srcObject = remoteStream;
    this.audioElement.muted = false;
    this.audioElement.volume = 1;
    void this.audioElement.play()
      .then(() => {
        this.debug("attachRemoteAudio:play:ok", {
          trackCount: remoteStream.getTracks().length,
        });
      })
      .catch((error) => {
        this.debug("attachRemoteAudio:play:error", {
          message: (error as Error)?.message ?? "unknown",
        });
      });

    window.setTimeout(() => {
      const firstAudioTrack = remoteStream.getAudioTracks()[0] ?? null;
      this.debug("attachRemoteAudio:postcheck", {
        streamTracks: remoteStream.getTracks().length,
        audioTracks: remoteStream.getAudioTracks().length,
        pcReceivers: pc.getReceivers().length,
        elementPaused: this.audioElement?.paused ?? null,
        elementReadyState: this.audioElement?.readyState ?? null,
        elementCurrentTime: this.audioElement?.currentTime ?? null,
        elementMuted: this.audioElement?.muted ?? null,
        elementVolume: this.audioElement?.volume ?? null,
        sinkId:
          (
            this.audioElement as HTMLAudioElement & { sinkId?: string }
          )?.sinkId ?? "default",
        firstAudioTrackEnabled: firstAudioTrack?.enabled ?? null,
        firstAudioTrackMuted: firstAudioTrack?.muted ?? null,
        firstAudioTrackReadyState: firstAudioTrack?.readyState ?? null,
      });
    }, 2500);
  }

  private resolveHoldTargetSession() {
    if (
      this.session?.state === SessionState.Established ||
      this.session?.state === SessionState.Establishing
    ) {
      return this.session;
    }

    if (
      this.heldSession?.state === SessionState.Established ||
      this.heldSession?.state === SessionState.Establishing
    ) {
      return this.heldSession;
    }

    return this.session;
  }

  private clearForegroundSession() {
    this.releaseLocalAudioTracks();
    this.pendingAnswerSuperviseMode = null;
    this.session = null;
    this.inviter = null;
    if (this.audioElement) {
      this.audioElement.srcObject = null;
    }
  }

  private restoreHeldSessionToForeground() {
    if (!this.heldSession) return;
    this.session = this.heldSession;
    this.heldSession = null;
    this.isHeldSessionOnHold = true;
    if (this.session.state === SessionState.Established) {
      this.attachRemoteAudio(this.session);
    }
  }

  private clearHeldSession() {
    if (this.heldSession) {
      this.sessionDirections.delete(this.heldSession);
    }
    this.heldSession = null;
    this.isHeldSessionOnHold = false;
  }

  private clearSession() {
    this.clearForegroundSession();
    this.clearHeldSession();
  }

  private clearAllPendingInvitations() {
    this.pendingInvitations.clear();
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect || !this.lastConnectOptions) return;
    if (this.reconnectTimer) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connectAndRegister(this.lastConnectOptions!).catch((error) => {
        console.error("SIP reconnect failed", { message: (error as Error)?.message });
        this.events.onRegistrationStatus?.("error");
        this.events.onError?.(error as Error);
        this.scheduleReconnect();
      });
    }, 2000);
  }
}
